import React, { useEffect, useState } from "react";
import { api, brl, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Wallet, QrCode, Copy, X, RefreshCw, DollarSign, ShieldAlert, PlayCircle, Settings2 } from "lucide-react";

const PURPLE = "#2B1E4A";

const STATUS = { paid: ["Paga", "bg-emerald-100 text-emerald-700"], partial: ["Parcial", "bg-amber-100 text-amber-700"], partial_late: ["Parcial atraso", "bg-amber-100 text-amber-800"], late: ["Em atraso", "bg-red-100 text-red-700"], pending: ["Pendente", "bg-gray-100 text-gray-600"] };

export default function AdminEletrosRecebimentos() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("");
  const [sigF, setSigF] = useState("");
  const [payModal, setPayModal] = useState(null);
  const [adjustModal, setAdjustModal] = useState(null);
  const [pix, setPix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [jobBusy, setJobBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusF ? { status: statusF, scope: "admin" } : { scope: "admin" };
      if (sigF) params.signature_status = sigF;
      setRows((await api.get("/eletros/installments", { params })).data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [statusF, sigF]);

  const runJob = async () => {
    setJobBusy(true);
    try {
      const { data } = await api.post("/eletros/responsibility/run");
      toast.success(`Job concluido: ${data.transferred} responsabilidade(s) transferida(s) · ${brl(data.total)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro no job"); }
    finally { setJobBusy(false); }
  };

  const genCharge = async (it) => {
    setBusy(true);
    try { const { data } = await api.post(`/eletros/installments/${it.id}/charge`); setPix({ ...data, inst: it }); toast.success("Cobranca PIX gerada"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar cobranca"); }
    finally { setBusy(false); }
  };

  const openInstallments = rows.filter((r) => r.status !== "paid");
  const projected = rows.reduce((a, r) => a + Number(r.commission_projected || 0), 0);
  const generated = rows.reduce((a, r) => a + Number(r.commission_generated || 0), 0);

  return (
    <div className="space-y-6 p-4 lg:p-8" data-testid="admin-eletros-recebimentos-page">
      <div className="flex items-center gap-3 rounded-2xl p-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "#F5B400" }}><Wallet size={24} className="text-[#2B1E4A]" /></div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#F5B400" }}>Eletros</div>
          <h1 className="font-display text-xl font-bold">Recebimentos Eletros</h1>
          <p className="text-[12px] text-white/70">Gere cobranca PIX (conta Eletros) e registre baixas manuais</p>
        </div>
        <button onClick={runJob} disabled={jobBusy} data-testid="receb-run-job" className="ml-auto flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white hover:bg-white/20 disabled:opacity-50">
          <PlayCircle size={16} /> {jobBusy ? "Rodando..." : "Rodar responsabilidade"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Mini label="Parcelas em aberto" value={String(openInstallments.length)} />
        <Mini label="Comissao gerada" value={brl(generated)} gold />
        <Mini label="Comissao projetada" value={brl(projected)} />
      </div>

      <div className="rounded-2xl border border-border bg-white p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="receb-status-filter" className="inp w-auto">
              <option value="">Todas</option>
              <option value="pending">Pendentes</option>
              <option value="late">Em atraso</option>
              <option value="partial">Parciais</option>
              <option value="paid">Pagas</option>
            </select>
            <select value={sigF} onChange={(e) => setSigF(e.target.value)} data-testid="receb-signature-filter" className="inp w-auto">
              <option value="">Assinatura: todas</option>
              <option value="PENDING_SIGNATURE">Pendentes de assinatura</option>
              <option value="SIGNED">Assinadas</option>
            </select>
          </div>
          <button onClick={load} className="flex items-center gap-1 rounded-xl border border-border px-3 py-2 text-sm font-semibold text-[#5B2CA0]"><RefreshCw size={14} /> Atualizar</button>
        </div>

        {loading ? <div className="py-8 text-center text-sm text-[#9A8A98]">Carregando...</div>
          : rows.length === 0 ? <div className="py-8 text-center text-sm text-[#9A8A98]">Nenhuma parcela Eletros.</div>
          : (
            <div className="space-y-2">
              {rows.map((it) => {
                const [label, cls] = STATUS[it.status] || STATUS.pending;
                return (
                  <div key={it.id} className="rounded-xl border border-border p-3" data-testid={`receb-inst-${it.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-brand-dark">{it.client_name || "—"} · Parcela {it.number}</div>
                        <div className="text-[12px] text-[#8A7788]">venc. {formatDate(it.due_date)} · original {brl(it.amount)} · saldo {brl(it.open_amount)}{it.overdue_days > 0 ? ` · atraso ${it.overdue_days}d` : ""}</div>
                        <div className="text-[11px] text-[#9A8A98]">Comissao gerada {brl(it.commission_generated)} · projetada {brl(it.commission_projected)}</div>
                        {it.responsibility_status !== "cliente" && (
                          <div className="mt-1 flex items-center gap-1 text-[11px] font-semibold text-red-600" data-testid={`receb-resp-${it.id}`}>
                            <ShieldAlert size={12} /> Responsabilidade {it.responsibility_status} · assumido {brl(it.responsibility_amount)} · recuperado {brl(it.responsibility_recovered)}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${cls}`}>{label}</span>
                        {it.alert_level && <span className="text-[13px]">{it.alert_level === "red" ? "🔴" : it.alert_level === "orange" ? "🟠" : "🟡"}</span>}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {it.status !== "paid" && (
                        <>
                          <button onClick={() => genCharge(it)} disabled={busy} data-testid={`receb-charge-${it.id}`} className="flex items-center gap-1 rounded-lg border border-[#5B2CA0] px-3 py-1.5 text-[12px] font-semibold text-[#5B2CA0] disabled:opacity-50"><QrCode size={13} /> Gerar PIX</button>
                          <button onClick={() => setPayModal(it)} data-testid={`receb-pay-${it.id}`} className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-[12px] font-semibold text-white" style={{ background: PURPLE }}><DollarSign size={13} /> Registrar pagamento</button>
                        </>
                      )}
                      {it.responsibility_status !== "cliente" && (
                        <button onClick={() => setAdjustModal(it)} data-testid={`receb-adjust-${it.id}`} className="flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-[12px] font-semibold text-[#6B5A69]"><Settings2 size={13} /> Ajuste manual</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </div>

      {payModal && <PayModal inst={payModal} onClose={() => setPayModal(null)} onSaved={() => { setPayModal(null); load(); }} />}
      {adjustModal && <AdjustModal inst={adjustModal} onClose={() => setAdjustModal(null)} onSaved={() => { setAdjustModal(null); load(); }} />}
      {pix && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={() => setPix(null)}>
          <div className="w-full max-w-xs rounded-3xl bg-white p-5 text-center shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="receb-pix-modal">
            <div className="mb-2 flex items-center justify-between"><span className="text-[11px] font-bold uppercase text-[#5B2CA0]">PIX {brl(pix.amount)}</span><button onClick={() => setPix(null)}><X size={16} /></button></div>
            {pix.qr_code_data_url && <img src={pix.qr_code_data_url} alt="QR" className="mx-auto my-2 h-44 w-44" />}
            {pix.copy_paste && <button onClick={() => { navigator.clipboard.writeText(pix.copy_paste); toast.success("Copiado"); }} className="mx-auto flex items-center gap-1 rounded-lg bg-[#F1ECF7] px-3 py-1.5 text-[12px] font-semibold text-[#5B2CA0]"><Copy size={13} /> Copiar codigo</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function PayModal({ inst, onClose, onSaved }) {
  const [amount, setAmount] = useState(inst.open_amount || "");
  const [method, setMethod] = useState("dinheiro");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const v = Number(amount);
    if (!v || v <= 0) { toast.error("Valor invalido"); return; }
    setSaving(true);
    try {
      await api.post("/eletros/payments", { installment_id: inst.id, amount: v, method });
      toast.success("Pagamento registrado");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="receb-pay-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Registrar pagamento</h2>
        <p className="mt-1 text-sm text-[#6B5A69]">{inst.client_name} · Parcela {inst.number} · saldo {brl(inst.open_amount)}</p>
        <div className="mt-4 space-y-3">
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Valor recebido (R$)</span>
            <input type="number" step="0.01" className="inp" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="receb-pay-amount" /></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Forma</span>
            <select className="inp" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="receb-pay-method">
              <option value="dinheiro">Dinheiro</option><option value="pix">PIX</option><option value="cartao">Cartao</option><option value="transferencia">Transferencia</option>
            </select></label>
          <p className="text-[11px] text-[#9A8A98]">A comissao sera gerada sobre o principal efetivamente recebido (mesma regra do Mercado Pago).</p>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} data-testid="receb-pay-save" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "..." : "Confirmar"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ inst, onClose, onSaved }) {
  const [amount, setAmount] = useState(inst.responsibility_outstanding || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const v = Number(amount);
    if (!v || v <= 0) { toast.error("Valor invalido"); return; }
    if (!reason.trim()) { toast.error("Motivo obrigatorio"); return; }
    setSaving(true);
    try {
      await api.post(`/eletros/installments/${inst.id}/responsibility/adjust`, { amount: v, reason: reason.trim() });
      toast.success("Ajuste registrado (lancamento compensatorio)");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="receb-adjust-modal">
        <h2 className="font-display text-lg font-bold text-brand-dark">Ajuste de responsabilidade</h2>
        <p className="mt-1 text-sm text-[#6B5A69]">{inst.client_name} · Parcela {inst.number} · sob responsabilidade {brl(inst.responsibility_outstanding)}</p>
        <p className="mt-2 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800">Gera um lancamento compensatorio (nunca apaga historico). Requer motivo.</p>
        <div className="mt-3 space-y-3">
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Valor a compensar (R$)</span>
            <input type="number" step="0.01" className="inp" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="receb-adjust-amount" /></label>
          <label className="block"><span className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Motivo (obrigatorio)</span>
            <input className="inp" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex: acordo com a cliente" data-testid="receb-adjust-reason" /></label>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={submit} disabled={saving} data-testid="receb-adjust-save" className="flex-1 rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }}>{saving ? "..." : "Confirmar ajuste"}</button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-3 text-sm font-semibold text-[#6B5A69]">Cancelar</button>
        </div>
      </div>
    </div>
  );
}


function Mini({ label, value, gold }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-4" data-testid={`receb-mini-${label}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">{label}</div>
      <div className="font-display text-lg font-bold" style={{ color: gold ? "#C9960A" : PURPLE }}>{value}</div>
    </div>
  );
}
